import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import {
  MY_VIRTUAL_COACHING_REQUEST,
  MY_VIRTUAL_COACHING_REQUESTS,
  PREPARE_PURCHASE_VIRTUAL_COACHING,
  SUBMIT_VIRTUAL_COACHING_VIDEOS,
  VIRTUAL_COACHING_OFFERING,
} from './queries'

export const getErrorMessage = error =>
  error?.graphQLErrors?.[0]?.message || error?.message || null

// `errorPolicy: 'all'` keeps GraphQL errors (the field is missing or the
// backend rejected the request) from turning into a blank screen.
export const useVirtualCoachingOffering = lessonTypeId =>
  useQuery(VIRTUAL_COACHING_OFFERING, {
    variables: { lessonTypeId },
    skip: !lessonTypeId,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  })

export const useMyVirtualCoachingRequests = ({ skip = false } = {}) =>
  useQuery(MY_VIRTUAL_COACHING_REQUESTS, {
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    errorPolicy: 'all',
  })

export const useMyVirtualCoachingRequest = requestId =>
  useQuery(MY_VIRTUAL_COACHING_REQUEST, {
    variables: { id: requestId },
    skip: !requestId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  })

export const useSubmitVirtualCoachingVideos = options =>
  useMutation(SUBMIT_VIRTUAL_COACHING_VIDEOS, options)

/**
 * Creates the Stripe payment intent for the offering once the golfer is signed
 * in. Mirrors `useGetClientSecret` for lesson packs, with an explicit error
 * state and `retry`.
 */
export const usePreparePurchaseVirtualCoaching = (
  lessonTypeId,
  { enabled = true } = {},
) => {
  const [prepare] = useMutation(PREPARE_PURCHASE_VIRTUAL_COACHING)
  const [attempt, setAttempt] = useState(0)
  const [state, setState] = useState({
    clientSecret: null,
    loading: false,
    errorMessage: null,
  })

  useEffect(() => {
    if (!enabled || !lessonTypeId) return undefined
    let cancelled = false
    setState({ clientSecret: null, loading: true, errorMessage: null })

    prepare({ variables: { lessonTypeId } })
      .then(({ data }) => {
        if (cancelled) return
        const result = data?.preparePurchaseVirtualCoaching
        const clientSecret = result?.paymentIntent?.clientSecret
        if (result?.success && clientSecret) {
          setState({ clientSecret, loading: false, errorMessage: null })
        } else {
          setState({
            clientSecret: null,
            loading: false,
            errorMessage:
              result?.message ||
              'We could not start your purchase. Please try again.',
          })
        }
      })
      .catch(error => {
        if (cancelled) return
        setState({
          clientSecret: null,
          loading: false,
          errorMessage:
            getErrorMessage(error) ||
            'We could not start your purchase. Please try again.',
        })
      })

    return () => {
      cancelled = true
    }
  }, [enabled, lessonTypeId, attempt, prepare])

  return { ...state, retry: () => setAttempt(count => count + 1) }
}
